import { Bell, BellOff, CarTaxiFront, FileText, ShieldCheck, Wallet } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Empty, Loadable, SecurityPanel, securityNoticeLabel, useAction, useAsync, useDebounced } from '@despachador/ui';
import type { InboxNotification } from '@despachador/api-client';
import { api } from '../lib/api';
import { useDriver } from '../store';
import { ChoiceGroup, SubPage } from '../components';
import { SilentAlertHelp } from '../panic';
import { playOnce, unlockAudio } from '../lib/alerts';
import { canInstall, disablePush, enablePush, hasNativePush, install, isNativeApp, isStandalone, openAppSettings, pushState, type PushState } from '../lib/device';

const PUSH_TEXT: Record<PushState, string> = {
  enabled: 'Activas en este teléfono.',
  disabled: 'Actívalas para enterarte de los servicios con la app en segundo plano.',
  denied: 'Actívalas en los ajustes del navegador para este sitio.',
  unsupported: 'Este navegador no las soporta: deja la app abierta en turno.',
  'no-server-key': 'Falta configurarlas en el servidor.',
};

/** Android app: the permission is the system's; whether the phone is woken with the app closed depends on FCM. */
function nativePushText(state: PushState, wakes: boolean): string {
  if (state === 'denied') return 'Bloqueadas: permítelas en los ajustes de la app. Sin ellas no verás los servicios con la pantalla apagada.';
  if (state !== 'enabled') return 'Desactivadas: actívalas para ver los servicios con la pantalla apagada o en otra app.';
  return wakes
    ? 'Activas: te avisamos de un servicio aunque la app esté cerrada.'
    : 'Activas mientras estás en turno. Si cierras la app del todo, no te llegarán avisos: déjala abierta.';
}

const TONES: Array<{ value: 'classic' | 'alarm' | 'soft'; label: string }> = [
  { value: 'classic', label: 'Clásico' },
  { value: 'alarm', label: 'Urgente' },
  { value: 'soft', label: 'Suave' },
];

/** How an offer reaches the driver: sound, vibration and notifications with the app closed. */
export function Alerts() {
  const { me, saveSettings, enableAudio } = useDriver();
  const { run } = useAction();
  const [push, setPush] = useState<PushState | null>(null);
  useEffect(() => void pushState().then(setPush), []);
  // the slider moves the value locally and saves once when the finger stops (was one request + toast per step)
  const [volume, setVolume] = useState<number | null>(null);
  const settledVolume = useDebounced(volume, 500);
  useEffect(() => {
    if (settledVolume !== null && settledVolume !== me?.notificationSettings.volume) void run(() => saveSettings({ volume: settledVolume }), 'Volumen guardado');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledVolume]);
  if (!me) return null;
  const s = me.notificationSettings;
  const save = (patch: Record<string, unknown>) => run(() => saveSettings(patch), 'Preferencia guardada');
  const pushOk = push === 'enabled';
  const pushText = isNativeApp ? nativePushText(push ?? 'disabled', hasNativePush && me.pushNative) : PUSH_TEXT[push ?? 'disabled'];
  const pushAction = isNativeApp ? (
    push === 'denied' ? (
      <button type="button" className="btn primary" onClick={() => void openAppSettings()}>
        Abrir ajustes
      </button>
    ) : !pushOk ? (
      <button type="button" className="btn primary" onClick={() => void enablePush(me.pushPublicKey, me.pushNative).then(setPush)}>
        Activar notificaciones
      </button>
    ) : null
  ) : pushOk ? (
    <button type="button" className="btn" onClick={() => void disablePush().then(() => setPush('disabled'))}>
      Desactivar en este teléfono
    </button>
  ) : push === 'unsupported' || push === 'denied' || push === 'no-server-key' ? null : (
    <button type="button" className="btn primary" onClick={() => void enablePush(me.pushPublicKey).then(setPush)}>
      Activar notificaciones
    </button>
  );
  return (
    <SubPage title="Sonido y alertas" flush>
      <section className="menu-group">
        <h2 className="micro-title">Cuando llega un servicio</h2>
        <div className="setting-list">
          <label className="setting-row">
            <span className="setting-name">Sonido</span>
            <input className="square-check" type="checkbox" checked={s.sound_enabled} onChange={(e) => void save({ soundEnabled: e.target.checked })} />
          </label>
          <div className="setting-row stacked">
            <span className="setting-name" id="tone-label">
              Tono
            </span>
            <ChoiceGroup variant="segmented" labelledBy="tone-label" value={s.sound_key} disabled={!s.sound_enabled} onChange={(v) => void save({ soundKey: v })} options={TONES} />
          </div>
          <div className="setting-row stacked">
            <label className="setting-volume" htmlFor="volume">
              <span className="setting-name">Volumen</span>
              <span className="mono">{volume ?? s.volume} %</span>
            </label>
            <input id="volume" type="range" min={10} max={100} step={10} value={volume ?? s.volume} disabled={!s.sound_enabled} onChange={(e) => setVolume(Number(e.target.value))} />
          </div>
          <label className="setting-row">
            <span className="setting-name">Vibración</span>
            <input className="square-check" type="checkbox" checked={s.vibration_enabled} onChange={(e) => void save({ vibrationEnabled: e.target.checked })} />
          </label>
        </div>
        <div className="page-pad">
          <button
            type="button"
            className="btn block tall"
            onClick={async () => {
              await unlockAudio();
              await enableAudio();
              playOnce(s.sound_key, volume ?? s.volume);
              navigator.vibrate?.(300);
            }}
          >
            <Bell size={18} aria-hidden /> Probar alerta
          </button>
        </div>
      </section>
      <section className="menu-group">
        <h2 className="micro-title">{isNativeApp ? 'Notificaciones del teléfono' : 'Con la app cerrada'}</h2>
        {pushOk ? (
          <div className="setting-note page-pad">
            <p>{pushText}</p>
            {pushAction}
          </div>
        ) : (
          <div className="warn-block">
            <BellOff size={20} aria-hidden />
            <div>
              <b>{push === 'denied' ? 'Notificaciones bloqueadas' : push === 'unsupported' || push === 'no-server-key' ? 'Notificaciones no disponibles' : 'Notificaciones desactivadas'}</b>
              <p>{pushText}</p>
              {pushAction}
            </div>
          </div>
        )}
        {canInstall() && !isStandalone() && (
          <div className="page-pad">
            <button type="button" className="btn block" onClick={() => void install()}>
              Instalar aplicación
            </button>
          </div>
        )}
      </section>
    </SubPage>
  );
}

// ---------------------------------------------------------------- notifications (DRV-010)
type Kind = 'servicios' | 'cuota' | 'documentos' | 'seguridad';
type KindFilter = 'todas' | Kind;
/** The kind of a notice, from its category (the API sends no kind of its own). */
const KIND_OF: Record<string, Kind> = {
  OFFER: 'servicios',
  OFFER_CLOSED: 'servicios',
  SERVICE_UPDATE: 'servicios',
  QUOTA: 'cuota',
  DOCUMENT_EXPIRY: 'documentos',
  SECURITY: 'seguridad',
  ACCOUNT: 'seguridad',
};
const KIND_LOOK: Record<Kind, { icon: ReactNode; tone: 'info' | 'ok' | 'warn' | 'danger' }> = {
  servicios: { icon: <CarTaxiFront size={18} aria-hidden />, tone: 'info' },
  cuota: { icon: <Wallet size={18} aria-hidden />, tone: 'ok' },
  documentos: { icon: <FileText size={18} aria-hidden />, tone: 'warn' },
  seguridad: { icon: <ShieldCheck size={18} aria-hidden />, tone: 'danger' },
};
const FILTERS: Array<{ value: KindFilter; label: string }> = [
  { value: 'todas', label: 'Todas' },
  { value: 'servicios', label: 'Servicios' },
  { value: 'cuota', label: 'Cuota' },
  { value: 'documentos', label: 'Documentos' },
  { value: 'seguridad', label: 'Seguridad' },
];
const PAGE = 20;
const MAX = 100;

/** "HOY", "AYER", "11 OCT": the day separator of the list. */
function dayLabel(d: Date, now: Date): string {
  if (d.toDateString() === now.toDateString()) return 'Hoy';
  if (d.toDateString() === new Date(now.getTime() - 86_400_000).toDateString()) return 'Ayer';
  return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short' }).format(d).replace(' de ', ' ').replace('.', '');
}
const timeOf = (d: Date) => new Intl.DateTimeFormat('es-CO', { hour: 'numeric', minute: '2-digit' }).format(d);

/** A quota notice that warns (due, overdue, suspended) instead of confirming (payment received). */
const quotaWarns = (n: InboxNotification) => /venc|pendiente|suspend|mora|gracia/i.test(`${n.title} ${n.body ?? ''}`);

/** What the central and the system told the driver, newest first, by day; filtered by kind in the URL. */
export function Notifications() {
  const [limit, setLimit] = useState(PAGE);
  const notif = useAsync(() => api.get<InboxNotification[]>(`/me/notifications?limit=${limit}`), [limit], { keepPrevious: true }); // «Cargar más» keeps the list on screen
  const [params, setParams] = useSearchParams();
  const kind: KindFilter = FILTERS.find((f) => f.value === params.get('tipo'))?.value ?? 'todas';
  const setKind = (v: KindFilter) => {
    const next = new URLSearchParams(params);
    if (v === 'todas') next.delete('tipo');
    else next.set('tipo', v);
    setParams(next, { replace: true });
  };
  return (
    <SubPage title="Notificaciones" flush>
      <div className="filters-bar">
        <ChoiceGroup label="Tipo de notificación" value={kind} onChange={setKind} options={FILTERS} />
      </div>
      <Loadable state={notif}>
        {(list) => {
          const shown = kind === 'todas' ? list : list.filter((n) => KIND_OF[n.category] === kind);
          if (!list.length)
            return (
              <div className="page-pad">
                <Empty title="No tienes notificaciones.">Aquí quedan los avisos de la central y de tu cuenta.</Empty>
              </div>
            );
          if (!shown.length)
            return (
              <div className="page-pad">
                <Empty
                  title="No hay notificaciones de este tipo."
                  action={
                    <button type="button" className="btn" onClick={() => setKind('todas')}>
                      Ver todas
                    </button>
                  }
                />
              </div>
            );
          const now = new Date();
          const groups: Array<{ day: string; items: InboxNotification[] }> = [];
          for (const n of shown) {
            const day = dayLabel(new Date(n.created_at), now);
            const g = groups[groups.length - 1];
            if (g && g.day === day) g.items.push(n);
            else groups.push({ day, items: [n] });
          }
          return (
            <>
              {groups.map((g) => (
                <section key={g.day} className="notif-day" aria-label={g.day}>
                  <h2 className="micro-title day-sep">{g.day}</h2>
                  <ul className="notif-list">
                    {g.items.map((n) => {
                      const k = KIND_OF[n.category];
                      const look = k ? KIND_LOOK[k] : { icon: <Bell size={18} aria-hidden />, tone: 'info' as const };
                      const tone = k === 'cuota' && quotaWarns(n) ? 'warn' : look.tone;
                      const at = new Date(n.created_at);
                      return (
                        <li key={n.id}>
                          <span className={`notif-icon ${tone}`}>{look.icon}</span>
                          <div className="notif-body">
                            <b>
                              {/* a change to the account's credentials: said with words, not only a color */}
                              {n.category === 'SECURITY' && <span className="badge warn">{securityNoticeLabel(n)}</span>} {n.title}
                            </b>
                            {n.body && <p>{n.body}</p>}
                            <time className="mono" dateTime={n.created_at}>
                              {timeOf(at)}
                            </time>
                            {n.category === 'DOCUMENT_EXPIRY' && typeof n.data?.url === 'string' && (
                              <Link to={n.data.url} className="btn sm">
                                {n.data.url === '/cuenta/vehiculo' ? 'Ver el vehículo' : 'Ver mis documentos'}
                              </Link>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
              {list.length >= limit && limit < MAX && (
                <div className="page-pad">
                  <button type="button" className="btn block" onClick={() => setLimit((l) => Math.min(MAX, l + PAGE))}>
                    Cargar más
                  </button>
                </div>
              )}
            </>
          );
        }}
      </Loadable>
    </SubPage>
  );
}

/** Second factor and open sessions of the driver's own account. */
export function Security({ onLogout }: { onLogout: () => void }) {
  return (
    <SubPage title="Seguridad y sesiones" flush>
      <SilentAlertHelp />
      <SecurityPanel api={api} onSignOut={onLogout} layout="driver" />
    </SubPage>
  );
}
